import { Editor, Element, Path, Range, Transforms } from 'slate';

export class TableUtil {
    constructor(editor) {
        this.editor = editor;
    }

    insertTable = (rows, columns) => {
        try {
            const [tableNode] = Editor.nodes(this.editor, {
                match: (n) => !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table' || n.type === 'tableWithoutHeaders'),
                mode: 'highest',
            });

            if (tableNode) {
                return;
            }
            if (!rows || !columns) {
                return;
            }

            const cellText = Array.from({ length: rows }, () => Array.from({ length: columns }, () => ''));
            const newTable = createTableNode(cellText, rows, columns);

            Transforms.insertNodes(this.editor, newTable, {
                mode: 'highest',
            });
            Transforms.insertNodes(this.editor, { type: 'paragraph', children: [{ text: '' }] }, { mode: 'highest' });
        } catch (err) {
            // Error in insert table operation
        }
    };

    removeTable = () => {
        try {
            Transforms.removeNodes(this.editor, {
                match: (n) => {
                    return !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table' || n.type === 'tableWithoutHeaders');
                },
                // mode:'highest'
            });
        } catch (err) {
            // Error in remove table operation
        }
    };

    removeColumn = (position) => {
        try {
            const { selection } = this.editor;
            if (!!selection && Range.isCollapsed(selection)) {
                const [tableNode] = Editor.nodes(this.editor, {
                    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && ['table-cell', 'table-header'].includes(n.type),
                });
                if (tableNode) {
                    const [[table, tablePath]] = Editor.nodes(this.editor, {
                        match: (n) =>
                            !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table' || n.type === 'tableWithoutHeaders'),
                    });
                    const [, currentCellPath] = tableNode;
                    // ✅ FIX: Get column index from current cell path (last index)
                    const columnIndex = currentCellPath[currentCellPath.length - 1];

                    // ✅ FIX: Iterate from bottom to top to prevent path shifts
                    Editor.withoutNormalizing(this.editor, () => {
                        for (let row = table.children.length - 1; row >= 0; row--) {
                            const cellPath = [...tablePath, row, columnIndex];
                            Transforms.removeNodes(this.editor, { at: cellPath });
                        }

                        // ✅ FIX: Get the UPDATED table node after removal to calculate correct column count
                        const [updatedTable] = Editor.node(this.editor, tablePath);
                        const actualColumns = updatedTable.children[0]?.children?.length || 0;

                        Transforms.setNodes(
                            this.editor,
                            { columns: actualColumns },
                            {
                                at: tablePath,
                            },
                        );
                    });
                }
            }
        } catch (err) {
            // Error in remove column operation
        }
    };

    removeRow = (position) => {
        try {
            Transforms.removeNodes(this.editor, {
                match: (n) => {
                    return !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table-row';
                },
                // mode:'highest'
            });
        } catch (err) {
            // Error in remove row operation
        }
    };

    insertRow = (position) => {
        try {
            const { selection } = this.editor;

            // ✅ FIX: Check if rows are selected via row selector
            let tableNode = null;
            let currentRow = null;

            if (this.editor.selectedRows?.length > 0) {
                // Use the first selected row
                const selectedRow = this.editor.selectedRows[0];
                const { tablePath, rowIndex } = selectedRow;

                const [[table]] = Editor.nodes(this.editor, {
                    at: tablePath,
                    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table' || n.type === 'tableWithoutHeaders'),
                });

                tableNode = [[table, tablePath]];
                currentRow = [...tablePath, rowIndex];
            } else if (!!selection && Range.isCollapsed(selection)) {
                // Original logic: find table-row from selection
                const [rowNode] = Editor.nodes(this.editor, {
                    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table-row',
                });
                if (!rowNode) {
                    return;
                }

                tableNode = Editor.nodes(this.editor, {
                    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table' || n.type === 'tableWithoutHeaders'),
                });
                const [, rowPath] = rowNode;
                currentRow = rowPath;
            } else {
                return;
            }

            if (tableNode) {
                const [[table, tablePath]] = tableNode;

                const path = position === 'after' ? Path.next(currentRow) : currentRow;

                // ✅ FIX: Get actual column count from first row instead of metadata
                const actualColumnCount = table.children[0]?.children?.length || 0;

                const newRow = createRow(Array(actualColumnCount).fill(''), -1);

                Editor.withoutNormalizing(this.editor, () => {
                    Transforms.insertNodes(this.editor, newRow, {
                        at: path,
                    });

                    // Only update metadata if it exists
                    if (typeof table.rows === 'number') {
                        Transforms.setNodes(
                            this.editor,
                            { rows: table.rows + 1 },
                            {
                                at: tablePath,
                            },
                        );
                    }
                });
            }
        } catch (err) {
            // Error in insert row operation
        }
    };

    insertColumn = (action) => {
        try {
            const { selection } = this.editor;
            if (!!selection && Range.isCollapsed(selection)) {
                const [tableNode] = Editor.nodes(this.editor, {
                    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && ['table-cell', 'table-header'].includes(n.type),
                });
                if (tableNode) {
                    const [[table, tablePath]] = Editor.nodes(this.editor, {
                        match: (n) =>
                            !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table' || n.type === 'tableWithoutHeaders'),
                    });
                    const [, currentCell] = tableNode;
                    const columnIndex = currentCell[currentCell.length - 1];
                    const insertColumnIndex = action === 'after' ? columnIndex + 1 : columnIndex;

                    // The last two indices of the path represents the row and column. We need to add one cell to each row starting from the first row
                    Editor.withoutNormalizing(this.editor, () => {
                        for (let row = 0; row < table.children.length; row++) {
                            // ✅ Check if first row has headers
                            const isFirstRow = row === 0;
                            const firstRowHasHeaders = table.children[0]?.children?.some((cell) => cell.type === 'table-header');
                            const cellType = isFirstRow && firstRowHasHeaders ? 'table-header' : 'table-cell';

                            const newCell = {
                                type: cellType,
                                children: [{ type: 'paragraph', children: [{ text: '' }] }],
                            };

                            const insertPath = [...tablePath, row, insertColumnIndex];

                            Transforms.insertNodes(this.editor, newCell, {
                                at: insertPath,
                            });
                        }

                        // ✅ FIX: Get the UPDATED table node after insertion to calculate correct column count
                        const [updatedTable] = Editor.node(this.editor, tablePath);
                        const actualColumns = updatedTable.children[0]?.children?.length || 0;

                        Transforms.setNodes(
                            this.editor,
                            { columns: actualColumns },
                            {
                                at: tablePath,
                            },
                        );
                    });
                }
            }
        } catch (err) {
            // Error in insert column operation
        }
    };

    convertRowToHeader = () => {
        try {
            const { selection } = this.editor;

            // Find the current row
            let rowPath = null;
            let tablePath = null;

            if (this.editor.selectedRows?.length > 0) {
                // Use the first selected row
                const selectedRow = this.editor.selectedRows[0];
                rowPath = [...selectedRow.tablePath, selectedRow.rowIndex];
                tablePath = selectedRow.tablePath;
            } else if (!!selection && Range.isCollapsed(selection)) {
                // Find row from current selection
                const [rowNode] = Editor.nodes(this.editor, {
                    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table-row',
                });

                if (!rowNode) {
                    console.warn('No row found for conversion');
                    return;
                }

                rowPath = rowNode[1];

                // Find parent table
                const [tableNode] = Editor.nodes(this.editor, {
                    match: (n) => !Editor.isEditor(n) && Element.isElement(n) && (n.type === 'table' || n.type === 'tableWithoutHeaders'),
                });

                if (!tableNode) {
                    console.warn('No table found');
                    return;
                }

                tablePath = tableNode[1];
            } else {
                return;
            }

            if (!rowPath || !tablePath) {
                return;
            }

            // Get the row node
            const [rowNode] = Editor.node(this.editor, rowPath);

            Editor.withoutNormalizing(this.editor, () => {
                // Convert all cells in the row to table-header
                rowNode.children.forEach((cell, cellIndex) => {
                    if (cell.type === 'table-cell') {
                        const cellPath = [...rowPath, cellIndex];
                        Transforms.setNodes(this.editor, { type: 'table-header' }, { at: cellPath });
                    }
                });

                // Change table type to 'table' (table with headers)
                Transforms.setNodes(this.editor, { type: 'table' }, { at: tablePath });
            });
        } catch (err) {
            console.error('Error converting row to header:', err);
        }
    };
}

/**
 * Creates a table row with cells.
 * @param {string[]} cellText - Array of text values for each cell in the row
 * @param {number} rowIndex - The index of this row in the table (0 = header row)
 * @returns {Object} Slate table-row node
 */
const createRow = (cellText, rowIndex = -1) => {
    try {
        const newRow = Array.from(cellText, (value) => createTableCell(value, rowIndex));
        return {
            type: 'table-row',
            children: newRow,
        };
    } catch (err) {
        // Error in create row
    }
};

/**
 * Creates a table cell (header or regular cell based on row index).
 * @param {string} text - The text content of the cell
 * @param {number} rowIndex - The row index (0 = header row, gets 'table-header' type)
 * @returns {Object} Slate table-cell or table-header node
 */
export const createTableCell = (text, rowIndex = -1) => {
    try {
        return {
            type: rowIndex === 0 ? 'table-header' : 'table-cell',
            children: [
                {
                    type: 'paragraph',
                    children: [{ text }],
                },
            ],
        };
    } catch (err) {
        // Error in create table cell
    }
};

/**
 * Creates a complete table node with rows and cells.
 * @param {string[][]} cellText - 2D array of cell text values
 * @param {number} rows - Number of rows
 * @param {number} columns - Number of columns
 * @returns {Object} Slate table node
 */
const createTableNode = (cellText, rows, columns) => {
    try {
        const tableChildren = Array.from(cellText, (rowData, rowIndex) => {
            const row = createRow(rowData, rowIndex);
            return row;
        });

        let tableNode = {
            type: 'table',
            children: tableChildren,
            rows,
            columns,
        };

        return tableNode;
    } catch (err) {
        // Error in create table node
    }
};
